import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Loader2, Send } from 'lucide-react';
import { SendTestMessageDialog } from './dialogs/SendTestMessageDialog';

export function TelegramTestDebug() {
  const [showTestMessageDialog, setShowTestMessageDialog] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [status, setStatus] = useState({
    bot: true,
    webhook: true,
    database: true,
  });

  const handleRefreshStatus = async () => {
    setIsRefreshing(true);
    try {
      // Simulate checking status
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success('Status refreshed');
    } catch (error) {
      toast.error('Failed to refresh status');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>🧪 Test & Debug Tools</CardTitle>
          <CardDescription>
            Test bot connectivity and send test messages
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <h4 className="font-medium">Connection Status</h4>
            <div className="grid gap-3">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm">Bot API</span>
                {status.bot ? (
                  <Badge variant="default" className="gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Online
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="gap-1">
                    <XCircle className="h-3 w-3" />
                    Offline
                  </Badge>
                )}
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm">Webhook</span>
                {status.webhook ? (
                  <Badge variant="default" className="gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Active
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="gap-1">
                    <XCircle className="h-3 w-3" />
                    Inactive
                  </Badge>
                )}
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm">Database</span>
                {status.database ? (
                  <Badge variant="default" className="gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Connected
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="gap-1">
                    <XCircle className="h-3 w-3" />
                    Disconnected
                  </Badge>
                )}
              </div>
            </div>

            <Button
              variant="outline"
              onClick={handleRefreshStatus}
              disabled={isRefreshing}
              className="w-full"
            >
              {isRefreshing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Refresh Status
            </Button>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium">Send Test Message</h4>
            <p className="text-sm text-muted-foreground">
              Send a test message to any registered admin user
            </p>
            <Button
              onClick={() => setShowTestMessageDialog(true)}
              className="w-full"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Test Message
            </Button>
          </div>

          <div className="p-4 border border-blue-500/50 rounded-lg bg-blue-500/10">
            <p className="text-sm font-medium">💡 Quick Tips:</p>
            <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
              <li>Test messages are logged in Message Logs tab</li>
              <li>Status check verifies bot connectivity</li>
              <li>Webhook must be active for bot to receive messages</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <SendTestMessageDialog
        open={showTestMessageDialog}
        onOpenChange={setShowTestMessageDialog}
      />
    </div>
  );
}
